// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallIndependentAmount } from '@hooks/mutations/collateral/useCallIndependentAmount';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import { ContractId } from '@daml/types';
import FormDialog from '../../../../../components/Dialog/FormDialog';
import TextInput, { TextInputLabel } from '../../../../../components/Form/TextInput';
import { MarketDataForm } from '../../../common/marketData/MarketDataForm';
import { useConvertToMarketData } from '../../../common/marketData/useConvertToMarketData';
import { useGetCollateralAgreement } from '../../../common/useGetCollateralAgreement';
import { useGetEligibleInstrumentsFromCollateralAgreement } from '../../../common/useGetEligibleInstrumentsFromCollateralAgreement';
import { useCallIndependentMarginForm } from './useCallIndependentMarginForm';

export type CallIndependentMarginDialogProps = {
  collateralAgreementCId: ContractId<CollateralAgreement>;
  onClose: () => void;
  open: boolean;
};

export const CallIndependentMarginDialog = ({
  collateralAgreementCId,
  open,
  onClose,
}: CallIndependentMarginDialogProps) => {
  const callIndependentMargin = useCallIndependentAmount();

  const { getCollateralAgreementByContractId } = useGetCollateralAgreement();

  const collateralAgreement = useMemo(() => {
    const agreement = getCollateralAgreementByContractId(collateralAgreementCId);

    if (!agreement) {
      throw new Error(`Collateral agreement not found for contract Id: ${collateralAgreementCId}!`);
    }

    return agreement;
  }, [getCollateralAgreementByContractId, collateralAgreementCId]);

  const eligibleInstruments = useGetEligibleInstrumentsFromCollateralAgreement({
    collateralAgreement,
  });

  const form = useCallIndependentMarginForm(collateralAgreement);
  const convertToMarketData = useConvertToMarketData();

  const onSubmit = useCallback(async () => {
    const createdAt = dayjs().toISOString();

    await callIndependentMargin({
      cid: collateralAgreement.contractId,
      argument: {
        reference: form.reference,
        tradeReference: form.tradeReference,
        createdAt,
        requiredValue: form.requiredValue,
        marketData: convertToMarketData(form.marketData, createdAt),
        recipient: collateralAgreement.payload.partyB,
      },
    });

    onClose();
  }, [callIndependentMargin, convertToMarketData, collateralAgreement, form, onClose]);

  return (
    <FormDialog
      submitName="Call"
      onCancel={onClose}
      open={open}
      title="Call Independent Margin"
      onSubmit={onSubmit}
      isSubmitDisabled={form.isSubmitDisabled}
    >
      <TextInput
        enableTopLabel
        placeholder="Enter an identifier for this margin call"
        label="Reference"
        value={form.reference}
        setValue={form.setReference}
      />
      <TextInput
        enableTopLabel
        placeholder="Enter a trade reference"
        label="Trade reference"
        value={form.tradeReference}
        setValue={form.setTradeReference}
      />
      <TextInput
        enableTopLabel
        placeholder="Enter required value"
        label="Required value"
        value={form.requiredValue}
        setValue={form.setRequiredValue}
        type="number"
      />
      <Box sx={{ mb: 1 }}>
        <TextInputLabel label="Market Data" required />
        <MarketDataForm
          value={form.marketData}
          onAdd={form.addPricingData}
          onUpdate={form.updatePricingData}
          onRemove={form.removePricingData}
          eligibleInstruments={eligibleInstruments}
        />
      </Box>
    </FormDialog>
  );
};
